import * as React from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase';
import { withStyles } from '@material-ui/core/styles';
import { Button, IconButton, MenuItem, Paper, TextField, Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import ScenarioManager from './ScenarioManager.component';
import { gradeSpanLabel } from './gradeLabels';

const styles = (theme) => ({
  root: {
    maxWidth: 900,
  },
  intro: {
    marginBottom: 16,
    color: theme.palette.text.secondary,
  },
  groupRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    marginBottom: 8,
  },
  form: {
    padding: 16,
    marginTop: 16,
    marginBottom: 16,
  },
  memberRow: {
    display: 'flex',
    alignItems: 'center',
  },
  campusField: {
    minWidth: 260,
    marginRight: 16,
  },
  gradeField: {
    minWidth: 110,
    marginRight: 16,
  },
  actions: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
  },
  cancelButton: {
    marginLeft: 8,
  },
  error: {
    color: theme.palette.error.main,
    marginTop: 8,
  },
  warning: {
    color: '#b26a00',
    marginTop: 8,
  },
});

const WHOLE = '';

// Numeric grade options within a campus's span ('03'-'12'); EE/PK/KG spans
// contribute no explicit-range options (whole campus only).
const gradeOptionsForCampus = (school) => {
  if (!school) return [];
  const low = parseInt(school.gradeStart, 10);
  const high = parseInt(school.gradeEnd, 10);
  if (isNaN(high)) return [];
  const start = isNaN(low) ? 1 : low;
  const options = [];
  for (let g = start; g <= high; g += 1) options.push(g);
  return options;
};

const emptyMember = () => ({ campusNumber: '', gradeStart: WHOLE, gradeEnd: WHOLE });

class CampusGroups extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    profile: PropTypes.object,
    groups: PropTypes.object,
    schoolList: PropTypes.object.isRequired,
    getCampusGroups: PropTypes.func.isRequired,
    groupSaved: PropTypes.func.isRequired,
    groupDeleted: PropTypes.func.isRequired,
  };

  state = {
    editingId: null, // null = closed, '' = new group, otherwise groupId
    name: '',
    members: [emptyMember()],
    busy: false,
    error: '',
    warnings: [],
  };

  componentDidMount() {
    const { groups, getCampusGroups } = this.props;
    if (groups == null) {
      getCampusGroups();
    }
  }

  campusChoices() {
    const { schoolList, profile } = this.props;
    return Object.entries(schoolList || {})
      .filter(([campusNumber]) => campusNumber !== (profile && profile.districtNumber))
      .map(([campusNumber, school]) => ({ campusNumber, school }))
      .sort((a, b) => (a.school.name || '').localeCompare(b.school.name || ''));
  }

  describeGroup(group) {
    const { schoolList } = this.props;
    const segments = group.memberSegments || [];
    const parts = segments.map((segment) => {
      const school = (schoolList || {})[segment.campusNumber];
      const name = school ? school.name : segment.campusNumber;
      // Whole-campus segments still show the campus's actual span so every
      // member reads the same way ("Grades 6-8", "Grade 5").
      const label =
        segment.gradeStart != null
          ? gradeSpanLabel(segment.gradeStart, segment.gradeEnd)
          : gradeSpanLabel(school && school.gradeStart, school && school.gradeEnd);
      return label ? `${name} (${label})` : name;
    });
    return parts.join(', ');
  }

  openNew = () => {
    this.setState({ editingId: '', name: '', members: [emptyMember()], error: '', warnings: [] });
  };

  openEdit = (groupId, group) => {
    const members = (group.memberSegments || []).map((segment) => ({
      campusNumber: segment.campusNumber,
      gradeStart: segment.gradeStart != null ? String(segment.gradeStart) : WHOLE,
      gradeEnd: segment.gradeEnd != null ? String(segment.gradeEnd) : WHOLE,
    }));
    this.setState({
      editingId: groupId,
      name: group.name || '',
      members: members.length ? members : [emptyMember()],
      error: '',
      warnings: [],
    });
  };

  closeForm = () => {
    this.setState({ editingId: null, error: '', warnings: [] });
  };

  updateMember = (index, field) => (event) => {
    const members = this.state.members.slice();
    members[index] = { ...members[index], [field]: event.target.value };
    if (field === 'campusNumber') {
      members[index].gradeStart = WHOLE;
      members[index].gradeEnd = WHOLE;
    }
    this.setState({ members });
  };

  addMember = () => {
    this.setState({ members: [...this.state.members, emptyMember()] });
  };

  removeMember = (index) => () => {
    const members = this.state.members.filter((m, i) => i !== index);
    this.setState({ members: members.length ? members : [emptyMember()] });
  };

  // Direct callable + try/catch so server-side validation and permission
  // errors surface inline (the global ASYNC_ERROR handler only console.logs).
  save = async () => {
    const { editingId, name, members } = this.state;
    const oneSided = members.some((m) => m.campusNumber && (m.gradeStart === WHOLE) !== (m.gradeEnd === WHOLE));
    if (oneSided) {
      this.setState({ error: 'Set both From and To grades, or leave both as "Whole campus".' });
      return;
    }
    const memberSegments = members
      .filter((m) => m.campusNumber)
      .map((m) => ({
        campusNumber: m.campusNumber,
        ...(m.gradeStart !== WHOLE && m.gradeEnd !== WHOLE
          ? { gradeStart: Number(m.gradeStart), gradeEnd: Number(m.gradeEnd) }
          : {}),
      }));
    if (!memberSegments.length) {
      this.setState({ error: 'Add at least one member campus.' });
      return;
    }
    this.setState({ busy: true, error: '', warnings: [] });
    try {
      const payload = { name, memberSegments };
      if (editingId) payload.groupId = editingId;
      const result = await firebase.functions().httpsCallable('saveCampusGroup')(payload);
      this.props.groupSaved(result.data);
      this.setState({ busy: false, editingId: null, warnings: (result.data && result.data.warnings) || [] });
    } catch (e) {
      this.setState({ busy: false, error: e.message || 'Could not save the group.' });
    }
  };

  delete = (groupId, groupName) => async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete campus group "${groupName}"?`)) return;
    this.setState({ busy: true, error: '' });
    try {
      await firebase.functions().httpsCallable('deleteCampusGroup')({ groupId });
      this.props.groupDeleted({ groupId });
      this.setState({ busy: false });
    } catch (e) {
      this.setState({ busy: false, error: e.message || 'Could not delete the group.' });
    }
  };

  renderMemberRow(member, index) {
    const { classes, schoolList } = this.props;
    const school = (schoolList || {})[member.campusNumber];
    const grades = gradeOptionsForCampus(school);
    return (
      <div className={classes.memberRow} key={index}>
        <TextField
          select
          label="Campus"
          className={classes.campusField}
          value={member.campusNumber}
          onChange={this.updateMember(index, 'campusNumber')}
          margin="dense"
        >
          {this.campusChoices().map(({ campusNumber, school: choice }) => (
            <MenuItem key={campusNumber} value={campusNumber}>
              {choice.name} (
              {gradeSpanLabel(choice.gradeStart, choice.gradeEnd) || `${choice.gradeStart}-${choice.gradeEnd}`})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="From grade"
          className={classes.gradeField}
          value={member.gradeStart}
          onChange={this.updateMember(index, 'gradeStart')}
          margin="dense"
          disabled={!grades.length}
        >
          <MenuItem value={WHOLE}>Whole campus</MenuItem>
          {grades.map((g) => (
            <MenuItem key={g} value={String(g)}>
              {g}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="To grade"
          className={classes.gradeField}
          value={member.gradeEnd}
          onChange={this.updateMember(index, 'gradeEnd')}
          margin="dense"
          disabled={!grades.length || member.gradeStart === WHOLE}
        >
          <MenuItem value={WHOLE}>Whole campus</MenuItem>
          {grades
            .filter((g) => member.gradeStart === WHOLE || g >= Number(member.gradeStart))
            .map((g) => (
              <MenuItem key={g} value={String(g)}>
                {g}
              </MenuItem>
            ))}
        </TextField>
        <IconButton onClick={this.removeMember(index)} title="Remove member">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </div>
    );
  }

  renderForm() {
    const { classes } = this.props;
    const { editingId, name, members, busy, error } = this.state;
    if (editingId === null) return null;
    return (
      <Paper className={classes.form}>
        <Typography variant="h6">{editingId ? 'Edit Campus Group' : 'New Campus Group'}</Typography>
        <TextField
          label="Group name"
          value={name}
          onChange={(event) => this.setState({ name: event.target.value })}
          margin="dense"
          style={{ minWidth: 300 }}
        />
        {members.map((member, index) => this.renderMemberRow(member, index))}
        <Button color="primary" size="small" onClick={this.addMember}>
          <AddIcon fontSize="small" />
          &nbsp;Add member campus
        </Button>
        <div className={classes.actions}>
          <Button variant="contained" color="primary" onClick={this.save} disabled={busy}>
            {busy ? 'Saving…' : 'Save Group'}
          </Button>
          <Button className={classes.cancelButton} onClick={this.closeForm} disabled={busy}>
            Cancel
          </Button>
        </div>
        {error && <Typography className={classes.error}>{error}</Typography>}
      </Paper>
    );
  }

  render() {
    const { classes, groups } = this.props;
    const { warnings, error, editingId } = this.state;
    const groupEntries = Object.entries(groups || {}).sort((a, b) => (a[1].name || '').localeCompare(b[1].name || ''));
    return (
      <div className={classes.root}>
        <Typography variant="h5" gutterBottom>
          Campus Groups
        </Typography>
        <Typography className={classes.intro}>
          A campus group runs the full A-F report over the combined students of its member campuses (or grade ranges),
          as one unofficial virtual campus.
        </Typography>
        {groupEntries.map(([groupId, group]) => (
          <Paper key={groupId} className={classes.groupRow}>
            <div>
              <Typography variant="subtitle1">{group.name}</Typography>
              <Typography variant="caption">{this.describeGroup(group)}</Typography>
            </div>
            <div>
              <IconButton onClick={() => this.openEdit(groupId, group)} title="Edit">
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={this.delete(groupId, group.name)} title="Delete">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          </Paper>
        ))}
        {!groupEntries.length && groups != null && (
          <Typography className={classes.intro}>No campus groups yet.</Typography>
        )}
        {editingId === null && (
          <Button variant="contained" color="primary" onClick={this.openNew}>
            New Campus Group
          </Button>
        )}
        {this.renderForm()}
        {editingId === null && error && <Typography className={classes.error}>{error}</Typography>}
        {warnings.map((warning) => (
          <Typography key={warning} className={classes.warning}>
            {warning}
          </Typography>
        ))}
        <ScenarioManager
          profile={this.props.profile}
          schoolList={this.props.schoolList}
          filters={this.props.filters}
          assessmentList={this.props.assessmentList}
          assessmentGroups={this.props.assessmentGroups}
        />
      </div>
    );
  }
}

export default withStyles(styles)(CampusGroups);
